import { SEO } from '../../components/SEO';
import { Link } from 'react-router-dom';

const sections = [
  { id: 'who-we-are', label: 'Who we are' },
  { id: 'data-we-collect', label: 'Data we collect' },
  { id: 'how-we-use', label: 'How we use data' },
  { id: 'third-parties', label: 'Third parties' },
  { id: 'retention', label: 'Retention' },
  { id: 'your-rights', label: 'Your rights' },
  { id: 'children', label: 'Children' },
  { id: 'changes', label: 'Changes' },
] as const;

export function PrivacyPolicy() {
  return (
    <>
      <SEO
        title="Privacy Policy | InterviewR"
        description="How InterviewR collects, uses, and protects your personal information when you use our mock interview platform."
        keywords="InterviewR privacy policy, data protection, GDPR"
        url={typeof window !== 'undefined' ? window.location.href : 'https://interview-r.com/privacy-policy'}
      />
      <main className="min-h-screen bg-stitch-canvas px-4 pb-16 pt-10 sm:px-6 md:pt-14 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="relative overflow-hidden rounded-stitch-2xl border border-stitch-border bg-gradient-to-br from-stitch-primary-soft via-stitch-surface to-stitch-accent-soft p-8 shadow-stitch-pop md:p-10">
            <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-stitch-primary/10 blur-3xl" />
            <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-stitch-primary">
                  <span className="material-symbols-outlined text-base">gavel</span>
                  Legal
                </p>
                <h1 className="mt-3 text-3xl font-bold tracking-tight text-stitch-fg md:text-4xl">Privacy Policy</h1>
                <p className="mt-2 text-sm text-stitch-muted">Last updated: April 14, 2026</p>
              </div>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 self-start rounded-stitch-lg border border-stitch-border bg-stitch-surface px-4 py-2.5 text-sm font-semibold text-stitch-fg shadow-stitch-card transition hover:bg-stitch-surface-2 md:self-auto"
              >
                <span className="material-symbols-outlined text-lg">arrow_back</span>
                Back to home
              </Link>
            </div>
          </div>

          <div className="mt-10 grid gap-10 lg:grid-cols-12">
            <aside className="lg:col-span-4 xl:col-span-3">
              <nav
                className="sticky top-24 space-y-1 rounded-stitch-xl border border-stitch-border bg-stitch-surface p-4 shadow-stitch-card"
                aria-label="Privacy policy sections"
              >
                <p className="mb-3 px-2 text-xs font-bold uppercase tracking-wider text-stitch-muted">On this page</p>
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="flex items-center gap-2 rounded-stitch-lg px-3 py-2 text-sm font-medium text-stitch-fg-soft transition hover:bg-stitch-surface-2 hover:text-stitch-fg"
                  >
                    <span className="material-symbols-outlined text-base text-stitch-muted">chevron_right</span>
                    {s.label}
                  </a>
                ))}
              </nav>
            </aside>

            <article className="lg:col-span-8 xl:col-span-9">
              <div className="rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card md:p-10">
                <p className="text-base leading-relaxed text-stitch-fg-soft">
                  InterviewR (&quot;we&quot;, &quot;us&quot;) operates the website{' '}
                  <a href="https://interview-r.com" className="font-medium text-stitch-primary hover:underline">
                    interview-r.com
                  </a>{' '}
                  (the &quot;Service&quot;). This page explains how we handle personal data when you use the Service.
                </p>

                <section id="who-we-are" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="flex items-center gap-2 text-xl font-bold text-stitch-fg">
                    <span className="flex h-9 w-9 items-center justify-center rounded-stitch-lg bg-stitch-primary-soft text-stitch-primary">
                      <span className="material-symbols-outlined text-xl">badge</span>
                    </span>
                    1. Who we are
                  </h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    The Service is operated by Nikoloz Turkishvili, Individual Entrepreneur, Tbilisi, Georgia. Contact:{' '}
                    <a href="mailto:info@interview-r.com" className="font-medium text-stitch-primary hover:underline">
                      info@interview-r.com
                    </a>
                    .
                  </p>
                </section>

                <section id="data-we-collect" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="flex items-center gap-2 text-xl font-bold text-stitch-fg">
                    <span className="flex h-9 w-9 items-center justify-center rounded-stitch-lg bg-stitch-accent-soft text-stitch-accent">
                      <span className="material-symbols-outlined text-xl">database</span>
                    </span>
                    2. Data we collect
                  </h2>
                  <ul className="mt-4 list-disc space-y-3 pl-6 text-stitch-fg-soft">
                    <li>
                      <strong className="text-stitch-fg">Account data:</strong> email, password (hashed), role, and
                      profile information you choose to provide.
                    </li>
                    <li>
                      <strong className="text-stitch-fg">Booking and payment data:</strong> information needed to
                      schedule interviews and process payments through our payment providers.
                    </li>
                    <li>
                      <strong className="text-stitch-fg">Usage and analytics:</strong> pages viewed and approximate
                      location (e.g. country) via analytics tools to improve the Service.
                    </li>
                    <li>
                      <strong className="text-stitch-fg">AI practice sessions:</strong> messages you send in AI
                      interview features are processed to generate responses. Demo sessions may be rate-limited by IP
                      address.
                    </li>
                    <li>
                      <strong className="text-stitch-fg">Cookies:</strong> we use cookies and similar technologies for
                      authentication, preferences, and analytics. See our cookie notice in the app.
                    </li>
                  </ul>
                </section>

                <section id="how-we-use" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">3. How we use data</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    We use data to provide and improve the Service, authenticate users, process bookings and payments,
                    communicate with you, secure our systems, comply with law, and—where you allow—show relevant ads
                    on public pages.
                  </p>
                </section>

                <section id="third-parties" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">4. Third parties</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    We rely on infrastructure and service providers (e.g. hosting, email, analytics, payment processors,
                    AI APIs such as Google Gemini, advertising networks). They process data only as needed to provide
                    their services to us.
                  </p>
                </section>

                <section id="retention" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">5. Retention</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    We retain information as long as your account is active or as needed for legal, tax, or security
                    purposes. You may request deletion of your account subject to applicable law and legitimate business
                    needs.
                  </p>
                </section>

                <section id="your-rights" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">6. Your rights</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    Depending on your location, you may have rights to access, correct, delete, or restrict processing
                    of your personal data, or to object to certain processing. Contact us at the email above to exercise
                    these rights.
                  </p>
                </section>

                <section id="children" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">7. Children</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    The Service is not directed at children under 16. We do not knowingly collect their personal data.
                  </p>
                </section>

                <section id="changes" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">8. Changes</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    We may update this policy from time to time. We will post the new version on this page with an
                    updated date.
                  </p>
                </section>

                <div className="mt-12 flex flex-wrap gap-3 border-t border-stitch-border pt-8">
                  <Link
                    to="/terms"
                    className="inline-flex items-center gap-2 rounded-stitch-lg bg-stitch-primary px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-stitch-primary/25 hover:bg-stitch-primary-hover"
                  >
                    Terms of Service
                    <span className="material-symbols-outlined text-base">arrow_forward</span>
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 rounded-stitch-lg border border-stitch-border px-4 py-2.5 text-sm font-semibold text-stitch-fg hover:bg-stitch-surface-2"
                  >
                    Home
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </main>
    </>
  );
}
